import {
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import { cn } from '../utils/cn';

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  footer?: ReactNode;
  /**
   * Klasy CIAŁA dialogu — ZASTĘPUJĄ domyślne `overflow-y-auto p-6`, a nie
   * dokładają się do nich (`cn` to gołe `clsx`, bez scalania Tailwinda, więc
   * `p-0` obok `p-6` daje wynik zależny od kolejności reguł w arkuszu).
   *
   * Istnieje dla okien, które są NARZĘDZIEM, a nie tekstem: edytor układów
   * wydruku wypełnia dialog własną siatką trzech kolumn i sam sobie zarządza
   * przewijaniem. Domyślne 24 px marginesu i przewijanie całego ciała
   * odbierały mu wtedy wysokość, przez którą płótno robiło się znaczkiem
   * pocztowym pośrodku pustego okna.
   */
  bodyClassName?: string;
}

// Elementy, po których wolno wędrować Tabem wewnątrz dialogu (pętla fokusu).
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      className,
      open,
      onClose,
      title,
      description,
      size = 'md',
      closeOnOverlayClick = true,
      closeOnEscape = true,
      showCloseButton = true,
      footer,
      bodyClassName,
      onKeyDown,
      children,
      ...props
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement | null>(null);

    // Handle escape key
    useEffect(() => {
      if (!closeOnEscape) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onClose, closeOnEscape]);

    // Prevent body scroll when modal is open
    useEffect(() => {
      if (open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [open]);

    // A11y: po otwarciu fokus wchodzi do dialogu, po zamknięciu wraca do
    // elementu, który był aktywny przed otwarciem (wyzwalacza).
    useEffect(() => {
      if (!open) return;
      const previouslyFocused = document.activeElement as HTMLElement | null;
      contentRef.current?.focus();
      return () => {
        previouslyFocused?.focus?.();
      };
    }, [open]);

    if (!open) return null;

    // Łączy wewnętrzny ref (fokus/pętla Tab) z ref-em przekazanym z zewnątrz.
    const setContentRef = (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    };

    // A11y: Tab/Shift+Tab zapętla fokus w obrębie dialogu (focus trap),
    // żeby klawiatura i czytnik ekranu nie wędrowały po tle za scrimem.
    const handleTrapKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Tab') {
        const container = contentRef.current;
        if (container) {
          const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
          if (focusable.length === 0) {
            e.preventDefault();
          } else {
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === container)) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && active === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      }
      onKeyDown?.(e);
    };

    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      full: 'max-w-[calc(100vw-2rem)]',
    };

    return (
      <div
        className="fixed inset-0 z-[var(--ids-z-modal,1200)] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-[color:var(--ids-scrim,rgba(2,6,16,0.66))] backdrop-blur-sm [animation:ids-fadein_var(--ids-dur-moderate)_var(--ids-ease-standard)]"
          onClick={closeOnOverlayClick ? onClose : undefined}
        />

        {/* Modal Content */}
        <div
          ref={setContentRef}
          tabIndex={-1}
          onKeyDown={handleTrapKeyDown}
          className={cn(
            'relative w-full bg-[color:var(--ids-surface,#141B2D)] rounded-[var(--ids-radius-xl,20px)] shadow-[var(--ids-elev-4,0_24px_60px_-12px_rgba(0,0,0,0.6))] border border-[color:var(--ids-border,rgba(255,255,255,0.08))]',
            // Wejście tokenowe (keyframes w aurora.css) — działa bez pluginu
            // tailwindcss-animate; prefers-reduced-motion wygasza globalnie.
            '[animation:ids-modalin_var(--ids-dur-moderate)_var(--ids-ease-emphasized)]',
            'max-h-[calc(100vh-2rem)] flex flex-col focus:outline-none',
            sizeClasses[size],
            className
          )}
          {...props}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between p-6 border-b border-[color:var(--ids-border,rgba(255,255,255,0.08))]">
              <div>
                {title && (
                  <h2
                    id="modal-title"
                    className="text-lg font-semibold text-[color:var(--ids-text,#F8FAFC)]"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="mt-1 text-sm text-[color:var(--ids-text-muted,#94A3B8)]">
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-2 -mr-2 -mt-2 text-[color:var(--ids-text-muted,#94A3B8)] hover:text-[color:var(--ids-text,#F8FAFC)] hover:bg-[color:var(--ids-surface-raised,#1A2336)] rounded-[var(--ids-radius-md,10px)] transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div
            className={bodyClassName ? cn('flex-1', bodyClassName) : 'flex-1 overflow-y-auto p-6'}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[color:var(--ids-border,rgba(255,255,255,0.08))]">
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Modal.displayName = 'Modal';

// Helper components for modal
export type ModalFooterProps = HTMLAttributes<HTMLDivElement>;

export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-end gap-3', className)} {...props}>
      {children}
    </div>
  )
);

ModalFooter.displayName = 'ModalFooter';
